import * as util from '#packages/util';
import * as stateManagement from '#packages/stateManagement';
import toggleMobileEditor from '../thunks/toggleMobileEditor';
import constants from '#packages/constants';
import type { MapStateToProps, MapDispatchToProps } from 'types/redux';
import type { PreviewType } from '#packages/stateManagement';
import {
  LARGE_SCREEN_SIZE,
  PREVIEW_HORIZONTAL_MARGIN,
  MOBILE_CANVAS_SIZE,
  MOBILE_MAX_RANGE,
  COMMONLY_SMALL_TABLET,
  COMMONLY_LARGE_LAPTOP,
  DESKTOP_CANVAS_SIZE,
  DESKTOP_WIDTH,
  TABLET_WIDTH,
  WIDE_DESKTOP_WIDTH,
} from '#packages/preview';
import { ZoomModeApiKey } from '#packages/apis';

const { MOBILE_FRAME_WIDTH } = constants.ROOT_COMPS.MOBILE_EDITOR;

interface OwnProps {}
export interface DeviceSwitchWithResizeStateProps {
  isMobileEditor: boolean;
  previewWidth: number;
  baselineWidth: number;
  initialWindowWidth: number;
  previewType: PreviewType;
}
export interface DeviceSwitchWithResizeDispatchProps {
  setPreviewWidth: (previewWidth: number) => void;
  initPreview: () => void;
  switchToMobile: () => void;
  switchToDesktop: () => void;
  switchToFullScreen: () => void;
  switchToLargeScreen: (windowWidth: number) => Promise<void>;
  disablePreviewResizeAnimation: typeof stateManagement.preview.actions.disablePreviewResizeAnimation;
}

const SLIDER_MARKS_TO_BI_PREVIEW_MODE: { [key: number]: string } = {
  [MOBILE_CANVAS_SIZE]: 'mobile editing size',
  [COMMONLY_SMALL_TABLET]: 'commonly small tablet',
  [TABLET_WIDTH]: 'large tablet',
  [DESKTOP_CANVAS_SIZE]: 'desktop editing size',
  [DESKTOP_WIDTH]: 'small laptop',
  [COMMONLY_LARGE_LAPTOP]: 'large laptop',
  [WIDE_DESKTOP_WIDTH]: 'large desktop',
};

export const mapPreviewTypeToBiPreviewMode = (
  previewType: PreviewType,
  previewWidth: number,
) => {
  if (SLIDER_MARKS_TO_BI_PREVIEW_MODE?.[previewWidth]) {
    return SLIDER_MARKS_TO_BI_PREVIEW_MODE?.[previewWidth];
  }

  return previewType;
};

const mapStateToProps: MapStateToProps<
  DeviceSwitchWithResizeStateProps,
  OwnProps
> = ({ editorAPI, state }) => {
  const isMobileEditor = editorAPI.isMobileEditor();
  const previewWidth = isMobileEditor
    ? MOBILE_FRAME_WIDTH
    : stateManagement.preview.selectors.getPreviewWidth(state);
  return {
    isMobileEditor,
    previewWidth,
    baselineWidth: editorAPI.site.getWidth && editorAPI.site.getWidth(),
    initialWindowWidth:
      stateManagement.domMeasurements.selectors.getEditorContentLayout(state)
        .width,
    previewType: stateManagement.preview.selectors.getPreviewType(state),
  };
};

const getEditorAPI = (
  dispatch: AnyFixMe,
  getState: AnyFixMe,
  { editorAPI }: AnyFixMe,
) => editorAPI;

const mapDispatchToProps: MapDispatchToProps<
  DeviceSwitchWithResizeDispatchProps,
  OwnProps
> = (dispatch) => {
  const editorAPI = dispatch(getEditorAPI);

  const exitZoomMode = () => {
    const zoomModeApi = editorAPI.host.getAPI(ZoomModeApiKey);
    if (zoomModeApi.isInZoomMode()) {
      zoomModeApi.exitZoomMode({
        originalScrollY: editorAPI.dsRead.site.getScroll().y || 0,
        isPreview: true,
      });
    }
  };

  return {
    setPreviewWidth: (previewWidth: number) => {
      dispatch(stateManagement.preview.actions.setPreviewWidth(previewWidth));
      exitZoomMode();
      if (previewWidth > MOBILE_CANVAS_SIZE && editorAPI.isMobileEditor()) {
        //TODO: switch MOBILE_CANVAS_SIZE to MOBILE_MAX_RANGE when tablet preview is ready
        dispatch(
          toggleMobileEditor(
            false,
            'topBar_switchToDesktop',
            'editor_view_mode_desktop',
          ),
        );
      }
      if (previewWidth <= MOBILE_CANVAS_SIZE && !editorAPI.isMobileEditor()) {
        //TODO: switch MOBILE_CANVAS_SIZE to MOBILE_MAX_RANGE when tablet preview is ready
        dispatch(
          toggleMobileEditor(
            true,
            'topBar_switchToMobile',
            'editor_view_mode_mobile',
          ),
        );
      }

      if (previewWidth <= MOBILE_MAX_RANGE) {
        dispatch(stateManagement.preview.actions.setPreviewType('mobile'));
      }
      if (previewWidth > MOBILE_MAX_RANGE) {
        dispatch(stateManagement.preview.actions.setPreviewType('desktop'));
      }
    },
    setPreviewType: (previewType: PreviewType) =>
      dispatch(stateManagement.preview.actions.setPreviewType(previewType)),
    initPreview: () => {
      const initialPreviewType = editorAPI.isMobileEditor()
        ? 'mobile'
        : 'desktop';
      const baselineWidth =
        editorAPI.site.getWidth && editorAPI.site.getWidth();
      dispatch(stateManagement.preview.actions.setPreviewWidth(baselineWidth));
      dispatch(
        stateManagement.preview.actions.setPreviewType(initialPreviewType),
      );
    },
    switchToMobile: () => {
      dispatch(stateManagement.preview.actions.disablePreviewResizeAnimation());
      dispatch(
        stateManagement.preview.actions.setPreviewWidth(MOBILE_CANVAS_SIZE),
      );
      dispatch(stateManagement.preview.actions.setPreviewType('mobile'));
      dispatch(
        toggleMobileEditor(
          true,
          'topBar_switchToDesktop',
          'editor_view_mode_mobile',
        ),
      );
      exitZoomMode();
    },
    switchToDesktop: () => {
      dispatch(stateManagement.preview.actions.setPreviewType('desktop'));
      dispatch(
        stateManagement.preview.actions.setPreviewWidth(DESKTOP_CANVAS_SIZE),
      );
      exitZoomMode();

      if (editorAPI.isMobileEditor()) {
        dispatch(
          toggleMobileEditor(
            false,
            'topBar_switchToDesktop',
            'editor_view_mode_desktop',
          ),
        );
      } else {
        dispatch(
          stateManagement.preview.actions.enablePreviewResizeAnimation(),
        );
      }
    },
    switchToFullScreen: () => {
      dispatch(stateManagement.preview.actions.setPreviewType('fullScreen'));
      exitZoomMode();

      if (editorAPI.isMobileEditor()) {
        dispatch(
          toggleMobileEditor(false, 'topBar_switchToDesktop', 'full_screen'),
        );
      } else {
        dispatch(
          stateManagement.preview.actions.enablePreviewResizeAnimation(),
        );
      }
    },
    switchToLargeScreen: async (windowWidth: number) => {
      dispatch(stateManagement.preview.actions.setPreviewType('largeScreen'));
      dispatch(
        stateManagement.preview.actions.setPreviewWidth(LARGE_SCREEN_SIZE),
      );
      if (editorAPI.isMobileEditor()) {
        dispatch(
          toggleMobileEditor(
            false,
            'topBar_switchToDesktop',
            'editor_view_mode_large_screen',
          ),
        );
      }

      const maxWidth = windowWidth - PREVIEW_HORIZONTAL_MARGIN * 2;
      const scaleToFit = maxWidth / LARGE_SCREEN_SIZE;

      const zoomModeApi = editorAPI.host.getAPI(ZoomModeApiKey);
      await zoomModeApi.enterZoomMode({
        zoomScale: scaleToFit.toFixed(2),
        originalScrollY: editorAPI.dsRead.site.getScroll().y || 0,
        isPreview: true,
      });
    },
    disablePreviewResizeAnimation:
      stateManagement.preview.actions.disablePreviewResizeAnimation,
  };
};

const {
  connect,
  STORES: { EDITOR_API },
} = util.hoc;

export default connect(EDITOR_API, mapStateToProps, mapDispatchToProps);
